import React, { useEffect, useState } from 'react';
import { NextPageWithLayout } from '@/pages/_app';
import { Layout } from '@/components/Common';
import { SEO_META } from '@/shared/seo';
import { SEO_META_PAGES } from '@/shared/types/seo';
import { logCommonView, logPageView } from '@/lib/service';
import CustomHead from '@/components/Common/Layout/Head';
import { WEBSITE_URL, STATIC_FILE_URL } from '@/shared/constants';
import PlatformPage from '@/components/Platform';
import { PIETRA_AI_CONTENT, PIETRA_AI_HEADER_CONTENT } from '@/shared/constants/platform-pages-constants';
import { EventSource } from '@/shared/constants/event';
import PlatformHeader from '@/components/Platform/PlatformHeader';
import { BrandVideos, Trustpilot } from '@/components/Home';
import { HOME_PAGE_BRAND_VIDEO_LIST } from '@/shared/constants/landing-pages';
import FeaturedIn from '@/components/ExistingBrandsLandingPage/FeaturedIn';
import FAQContainer from '@/components/Common/FAQContainer';
import { useRouter } from 'next/router';
import ExpandYourRetailNetwork from '@/components/Platform/RetailConnect/ExpandYourRetailNetwork';
import cN from 'classnames';
import { map } from 'lodash-es';
import Image from 'next/image';
import './index.less';
import { SEO_PLATFORM_PIETRA_AI } from '@/shared/constants/seo';
import Script from 'next/script';
import { faqJsonLd } from '@/shared/seo/jsonld';

const PietraAiPage: NextPageWithLayout<any> = ({}) => {
  const [email, setEmail] = useState<string>('');

  const router = useRouter();

  const handleStart = () => {
    if (email) {
      logCommonView(EventSource.CONTENT_STUDIO_SIGNUP_FORM, email);
      router.push({ pathname: '/sell-on-pietra/register', query: { email } });
      return;
    }
    router.push({ pathname: '/sell-on-pietra/register' });
  };

  useEffect(() => {
    logPageView({
      pageUrl: window.location.href || '',
      routeName: window.location.pathname,
      pageTitle: 'Create professional product visuals for your brand! | Pietra',
    });
  }, []);

  return (
    <>
      <PlatformHeader
        content={PIETRA_AI_HEADER_CONTENT}
        customTitle={
          <h1 className={cN('title', PIETRA_AI_HEADER_CONTENT?.titleClass)}>
            Transform your business with the first AI for e-commerce
          </h1>
        }
        customActionBtn={
          <div className="pietra-ai-custom-action-btn-container">
            <input
              type="text"
              placeholder="Enter your email"
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleStart();
                }
              }}
            />
            <button className="action-btn-container" onClick={handleStart}>
              <span>Start for free</span>
            </button>
          </div>
        }
        customHero={
          <div className="hero-banner-container">
            {map(PIETRA_AI_HEADER_CONTENT.heroBanner, ({ title, description, imageUrl }) => (
              <div className="hero-banner-item" key={title}>
                <Image
                  src={imageUrl}
                  width={575}
                  height={575}
                  sizes="(min-width: 1024px) 575px, (min-width: 768px) 460px, (min-width: 640px) 387px, calc(100vw -32px)"
                  alt={description}
                />
                <div className="hero-banner-item-info">
                  <div className="title">{title}</div>
                  <div className="description">{description}</div>
                </div>
              </div>
            ))}
          </div>
        }
      />
      <div className="pietra-ai-rating-wrap">
        <Trustpilot />
      </div>
      <BrandVideos
        className="platform-brand-video-container"
        brandTitle="300,000+ brands & entrepreneurs choose Pietra"
        brandList={HOME_PAGE_BRAND_VIDEO_LIST}
      />
      <FeaturedIn />
      <ExpandYourRetailNetwork
        title={`“From product launches to packaging, Pietra AI has been our go-to partner for growth.”`}
        description="- Mitali, Transcendence Coffee"
        btnText="Start for free"
        customClass="pietra-ai"
      />
      <PlatformPage content={PIETRA_AI_CONTENT} utmSource="pietra_ai" />
      <FAQContainer faq={SEO_PLATFORM_PIETRA_AI} className="pietra-ai" />
      <Script
        id="faq-seo-platform-pietra-ai"
        type="application/ld+json"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqJsonLd(SEO_PLATFORM_PIETRA_AI)),
        }}
      />
    </>
  );
};

PietraAiPage.getLayout = (page: React.ReactElement) => {
  return (
    <>
      <CustomHead
        title="Create professional product visuals for your brand! | Pietra"
        description="Train AI models on your products and create marketing content."
        url={`${WEBSITE_URL}/platform/pietra-ai`}
        image={`${STATIC_FILE_URL}/44aa4fe822ee9303ac7aad4f9810d3c4.png`}
      />
      <Layout
        structured={SEO_META[SEO_META_PAGES.IDEATION].jsonld({
          url: typeof window === 'object' ? window.location.href : '',
        })}
      >
        {page}
      </Layout>
    </>
  );
};

export default PietraAiPage;
